import { useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import { CLAIMS_SORT_OPTIONS } from '../domain/sort';
import type { ClaimsSortId } from '../types';
import checkIcon from '../assets/icon-sort-check.svg';

interface SortMenuProps {
  open: boolean;
  onClose: () => void;
  value: ClaimsSortId;
  onChange: (id: ClaimsSortId) => void;
}

export function SortMenu({ open, onClose, value, onChange }: SortMenuProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: PointerEvent) => {
      const target = e.target as Node | null;
      if (ref.current?.contains(target)) return;
      if (target instanceof Element && target.closest('[data-sort-root]')) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      ref={ref}
      role="listbox"
      className="absolute right-0 top-[calc(100%+8px)] z-30 flex w-[203px] flex-col rounded-xl bg-white p-1 shadow-[0_16px_24px_rgba(26,22,19,0.2)]"
    >
      {CLAIMS_SORT_OPTIONS.map((option) => {
        const active = option.id === value;
        return (
          <button
            key={option.id}
            type="button"
            role="option"
            aria-selected={active}
            onClick={() => {
              onChange(option.id);
              onClose();
            }}
            className={cn(
              'flex w-full items-center justify-between rounded-lg py-2 pl-4 pr-2 text-left text-[15px] font-medium leading-5 text-mf-neutral-900',
              active && 'bg-[#e6f7ec]',
            )}
          >
            {option.label}
            {active && (
              <span className="flex size-5 shrink-0 items-center justify-center overflow-hidden">
                <img src={checkIcon} alt="" className="size-[15px]" />
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
